import { useState } from 'react';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/dahcdnroh653ivfvnr90';

type Status = 'idle' | 'submitting' | 'success' | 'error';

const topics = ['招聘线索', '园区线索', '被骗经历', '家属求助', '内容纠错', '合作与转载'];

export default function ContactForm() {
  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (status === 'submitting') return;

    const form = event.currentTarget;
    const data = new FormData(form);

    const honeypot = String(data.get('website_alt') || '').trim();
    if (honeypot) {
      setStatus('success');
      setFormError('');
      form.reset();
      return;
    }
    data.delete('website_alt');

    const payload = new URLSearchParams();
    data.forEach((value, key) => {
      if (typeof value === 'string' && value.trim() !== '') {
        payload.append(key, value);
      }
    });

    setStatus('submitting');
    setFormError('');

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const isSpam = /spam/i.test(serverMsg) || /spam/i.test(responseText);
      const isSuccess = response.ok && parsed?.code === 'OK' && !isSpam;

      if (isSuccess) {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试，或直接发送邮件给我们。');
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，提交未成功。请检查网络后重试。');
    }
  };

  const fieldClass =
    'w-full rounded-md border border-background-200 bg-background-50 px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200';

  return (
    <form
      id="lead-form"
      data-readdy-form
      onSubmit={handleSubmit}
      noValidate
      className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-7"
    >
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="lead-name" className="mb-2 block text-sm font-medium text-foreground-800">
            称呼
          </label>
          <input
            id="lead-name"
            name="name"
            type="text"
            required
            maxLength={40}
            placeholder="如何称呼您"
            className={fieldClass}
          />
        </div>
        <div>
          <label htmlFor="lead-email" className="mb-2 block text-sm font-medium text-foreground-800">
            联系邮箱
          </label>
          <input
            id="lead-email"
            name="email"
            type="email"
            required
            placeholder="用于接收核实结果"
            className={fieldClass}
          />
        </div>
      </div>

      <div className="mt-5">
        <label htmlFor="lead-topic" className="mb-2 block text-sm font-medium text-foreground-800">
          线索类型
        </label>
        <select id="lead-topic" name="topic" required defaultValue="" className={fieldClass}>
          <option value="" disabled>
            请选择线索类型
          </option>
          {topics.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </div>

      <div className="mt-5">
        <label htmlFor="lead-message" className="mb-2 block text-sm font-medium text-foreground-800">
          线索详情
        </label>
        <textarea
          id="lead-message"
          name="message"
          required
          maxLength={500}
          rows={5}
          placeholder="请尽量描述时间、地点与主要经过，避免填写个人敏感信息（500 字以内）"
          className={`${fieldClass} resize-none`}
        ></textarea>
      </div>

      {/* anti-spam field */}
      <input
        type="text"
        name="website_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="pref-note"
      />

      <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="flex items-start gap-2 text-xs leading-relaxed text-foreground-500">
          <i className="ri-lock-line mt-0.5" aria-hidden="true"></i>
          <span>线索默认匿名处理，我们不会公开提供者的任何身份信息。</span>
        </p>
        <button
          type="submit"
          disabled={status === 'submitting'}
          className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {status === 'submitting' ? (
            <>
              <i className="ri-loader-4-line animate-spin" aria-hidden="true"></i>
              正在提交
            </>
          ) : (
            <>
              <i className="ri-send-plane-line" aria-hidden="true"></i>
              提交线索
            </>
          )}
        </button>
      </div>

      {status === 'success' && (
        <p className="mt-5 flex items-center gap-2 rounded-md border border-secondary-300 bg-secondary-50 px-4 py-3 text-sm text-secondary-900">
          <i className="ri-checkbox-circle-line" aria-hidden="true"></i>
          感谢提供线索，我们已收到并会尽快核实。
        </p>
      )}
      {status === 'error' && (
        <p className="mt-5 flex items-center gap-2 rounded-md border border-accent-300 bg-accent-50 px-4 py-3 text-sm text-accent-800">
          <i className="ri-error-warning-line" aria-hidden="true"></i>
          {formError}
        </p>
      )}
    </form>
  );
}